import React, {useCallback, useEffect, useState} from 'react';
import {useParams} from 'react-router-dom';

import {type Property} from '../../../utilTypes';
import PropertyHeader from '../PropertyHeader';
import {type ProjectOption} from '../types';
import {
  type AliasOrVersionOption,
  type ArtifactOption,
  parseArtifactPath,
} from '../util';
import {AliasOrVersionSelector} from './AliasOrVersionSelector';
import * as S from './ArtifactPathSelector.styles';
import {ArtifactSelector} from './ArtifactSelector';
import {ProjectSelector} from './ProjectSelector';

interface ArtifactPathSelectorProps {
  property: Property;
  value?: string;
  onChange?: (value: string) => void;
}

interface ParsedArtifactPathComplete {
  entity: string;
  project: string;
  artifact: string;
  alias: string;
}

function parseArtifactPathComplete(
  value: string | undefined
): ParsedArtifactPathComplete | null {
  if (!value) {
    return null;
  }
  const parsed = parseArtifactPath(value);
  if (parsed.entity && parsed.project && parsed.artifact && parsed.alias) {
    return {
      entity: parsed.entity,
      project: parsed.project,
      artifact: parsed.artifact,
      alias: parsed.alias,
    };
  }
  return null;
}

export const ArtifactPathSelector = ({
  property,
  value,
  onChange,
}: ArtifactPathSelectorProps) => {
  const {entityName, projectName} = useParams<{
    entityName: string;
    projectName: string;
  }>();

  // parse the default value once to use for initialization
  const parsedDefaultValue = parseArtifactPathComplete(value);

  const [selectedProject, setSelectedProject] = useState<ProjectOption | null>(
    () => {
      if (parsedDefaultValue) {
        return {
          entityName: parsedDefaultValue.entity,
          projectName: parsedDefaultValue.project,
          label: `${parsedDefaultValue.entity}/${parsedDefaultValue.project}`,
          value: `${parsedDefaultValue.entity}/${parsedDefaultValue.project}`,
        };
      }

      // fall back to URL params if no valid value prop
      if (entityName && projectName) {
        return {
          entityName,
          projectName,
          label: `${entityName}/${projectName}`,
          value: `${entityName}/${projectName}`,
        };
      }
      return null;
    }
  );
  const [selectedArtifact, setSelectedArtifact] =
    useState<ArtifactOption | null>(() => {
      if (parsedDefaultValue) {
        return {
          name: parsedDefaultValue.artifact,
          label: parsedDefaultValue.artifact,
          value: parsedDefaultValue.artifact,
          aliasesOrVersions: [], // will be populated when artifact data is fetched
        };
      }
      return null;
    });
  const [selectedVersion, setSelectedVersion] =
    useState<AliasOrVersionOption | null>(() => {
      if (parsedDefaultValue) {
        const isVersion = /^v\d+$/.test(parsedDefaultValue.alias);
        return {
          id: parsedDefaultValue.alias,
          alias: parsedDefaultValue.alias,
          label: parsedDefaultValue.alias,
          value: parsedDefaultValue.alias,
          type: isVersion ? 'version' : 'alias',
        };
      }
      return null;
    });

  const handleProjectSelect = useCallback((project: ProjectOption | null) => {
    setSelectedProject(project);
    setSelectedArtifact(null);
    setSelectedVersion(null);
  }, []);

  // artifact path is only valid with all three selections
  useEffect(() => {
    if (onChange) {
      if (!selectedProject || !selectedArtifact || !selectedVersion) {
        onChange('');
      } else {
        const artifactPath = `${selectedProject.entityName}/${selectedProject.projectName}/${selectedArtifact.name}:${selectedVersion.alias}`;
        onChange(artifactPath);
      }
    }
  }, [selectedProject, selectedArtifact, selectedVersion, onChange]);

  const handleArtifactSelect = useCallback(
    (artifact: ArtifactOption | null) => {
      setSelectedArtifact(artifact);
      setSelectedVersion(null);
    },
    []
  );

  // when the artifact options are loaded, check if our selected artifact
  // matches one of them and update it with complete data (including aliases/versions)
  const handleArtifactOptionsLoaded = useCallback(
    (options: ArtifactOption[]) => {
      if (!selectedArtifact) {
        return;
      }

      // case 1: artifact was initialized from default value with empty aliasesOrVersions
      // need to hydrate it with complete data
      if (selectedArtifact.aliasesOrVersions.length === 0) {
        const matchingArtifact = options.find(
          opt => opt.name === selectedArtifact.name
        );
        if (matchingArtifact) {
          setSelectedArtifact(matchingArtifact);

          // if we already have a version selected (from default value), hydrate it with complete data
          if (selectedVersion) {
            const allVersions = matchingArtifact.aliasesOrVersions.flatMap(
              group => group.options
            );
            const matchingVersion = allVersions.find(
              v => v.alias === selectedVersion.alias
            );
            if (matchingVersion) {
              setSelectedVersion(matchingVersion);
            }
          }
        }
      }
    },
    [selectedArtifact, selectedVersion]
  );

  const handleVersionSelect = (alias: AliasOrVersionOption | null) => {
    setSelectedVersion(alias);
  };

  return (
    <S.Container>
      <PropertyHeader property={property} />
      <S.ArtifactPathContainer>
        <S.DropdownItem>
          <S.DropdownItemLabelText>Project</S.DropdownItemLabelText>
          <ProjectSelector
            value={selectedProject}
            onProjectSelect={handleProjectSelect}
            initialSearchString={
              parsedDefaultValue
                ? parsedDefaultValue.project
                : entityName && projectName
                  ? projectName
                  : undefined
            }
          />
        </S.DropdownItem>
        <S.DropdownItem>
          <S.DropdownItemLabelText>Artifact</S.DropdownItemLabelText>
          <ArtifactSelector
            key={selectedProject?.value || 'no-project'}
            entityName={selectedProject?.entityName}
            projectName={selectedProject?.projectName}
            value={selectedArtifact}
            onArtifactSelect={handleArtifactSelect}
            onArtifactOptionsLoaded={handleArtifactOptionsLoaded}
            initialSearchString={
              parsedDefaultValue ? parsedDefaultValue.artifact : undefined
            }
          />
        </S.DropdownItem>
        <S.DropdownItem>
          <S.DropdownItemLabelText>Version</S.DropdownItemLabelText>
          <AliasOrVersionSelector
            key={selectedArtifact?.value || 'no-artifact'}
            selectedArtifact={selectedArtifact}
            value={selectedVersion}
            onVersionSelect={handleVersionSelect}
          />
        </S.DropdownItem>
      </S.ArtifactPathContainer>
    </S.Container>
  );
};
